import type {
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  FocusEventHandler,
  ForwardedRef,
} from 'react';
import { createRef, forwardRef, useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';

import styles from './Select.module.css';

import { useElementState } from '../../hooks/useElementState';
import { useForwardedRef } from '../../hooks/useForwardedRef';
import { Caret, Chevron, ChevronAccordion } from '../../icons';
import type { FieldLabelBase } from '../FieldLabel';
import { FieldLabel } from '../FieldLabel';

export type SelectIconName = 'caret' | 'chevron';

/** The props of {@link Select}. */
export interface SelectProps
  extends FieldLabelBase, Omit<ComponentPropsWithoutRef<'select'>, 'multiple'> {
  /** A Select always displays the label in the entered transition state. */
  in?: true | undefined;
  /** Name of the icon to display on the right. */
  icon?: SelectIconName;
  /** Apply bold font-weight to selection. */
  bold?: boolean;
  /** Toggle invalid state. */
  invalid?: boolean;
  /** Display highlighted border state */
  highlighted?: boolean;
}

/**
 * Render a custom select component that uses native dropdown features.
 *
 * @param props - The {@link SelectProps} to render the select field.
 * @see https://corgi-x.tfd.engineering/components/select
 */
export const Select = forwardRef(Select_);

function Select_(
  props: SelectProps,
  forwardedRef?: ForwardedRef<HTMLSelectElement>
) {
  const {
    label,
    icon = 'caret',
    hideLabel = false,
    bold = false,
    invalid,
    className,
    children,
    highlighted,
    ...selectProps
  } = props;

  const [state, handlers] = useElementState({
    disabled: props.disabled || props['aria-disabled'] === true,
    highlighted,
    invalid,
  });

  const [labelIn, setLabelIn] = useState(Boolean(props.in));
  const ref = useForwardedRef(forwardedRef ?? createRef<HTMLSelectElement>());

  const refreshLabelIn = useCallback(() => {
    // the caller has asked to keep it open permanently
    if (props.in) {
      return;
    }

    if (!ref.current) {
      return;
    }

    const selectedOption = ref.current.options[ref.current.selectedIndex];
    setLabelIn(Boolean(selectedOption?.text));
  }, [props.in, ref]);

  // Override the in prop only if user set it up to true
  useEffect(() => {
    if (props.in) {
      setLabelIn(Boolean(props.in));
    }
  }, [props.in]);

  useEffect(() => {
    refreshLabelIn();
  }, [refreshLabelIn, children]);

  const handleFocus: FocusEventHandler<HTMLSelectElement> = e => {
    handlers.onFocus(e);
    props.onFocus?.(e);
  };

  const handleBlur: FocusEventHandler<HTMLSelectElement> = e => {
    handlers.onBlur(e);
    props.onBlur?.(e);
  };

  const handleChange: ChangeEventHandler<HTMLSelectElement> = e => {
    refreshLabelIn();
    props.onChange?.(e);
  };

  return (
    <FieldLabel
      in={labelIn}
      state={state}
      label={label}
      className={className}
      hideLabel={hideLabel}
    >
      <select
        {...selectProps}
        ref={ref}
        className={classNames(
          styles.select,
          bold ? styles.bold : styles.regular
        )}
        aria-invalid={invalid}
        disabled={state === 'disabled'}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
      >
        {children}
      </select>
      <span className={classNames(styles.iconRoot, styles[icon])} aria-hidden>
        <span className={styles.defaultIcon}>
          {icon === 'chevron' ? (
            <Chevron aria-hidden fill="kale-3" size={36} />
          ) : (
            <Caret aria-hidden fill="kale-3" size={36} />
          )}
        </span>
        <span className={styles.foundationIcon}>
          <ChevronAccordion aria-hidden baseColor="Sage700" size={24} />
        </span>
      </span>
    </FieldLabel>
  );
}
